import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { checkImageAppropriate, checkTextAppropriate } from "./utils/ContentModeration";
import { ethers } from "ethers";
import { Dialog, DialogTitle, DialogContent, TextField, Button, CircularProgress, IconButton, 
         Checkbox, FormControlLabel, Select, MenuItem, Typography, Box, Chip, Paper,
         List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction, Divider, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import DescriptionIcon from '@material-ui/icons/Description';
import ImageIcon from '@material-ui/icons/Image';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { SERVER_NAME } from './config';

import E3DTokenABI from "./E3DToken_ABI.json";
import NFTManagerABI from "./E3DNFTManager_ABI.json";
import { getSubscriptionDetails } from './web3/contractInteractions';
import { isDebugFakePremium, getMockSubscriptionDetails } from './web3/debugContractOverrides';

// Custom styles to match swap/stake dialogs
const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    backgroundColor: '#181818',
    color: 'white',
    maxWidth: '600px',
    width: '100%',
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    border: '1px solid #333',
    borderRadius: '10px',
  },
  dialogTitle: {
    backgroundColor: '#232323',
    color: 'white',
    '& h2': {
      fontSize: '1.75rem',
      fontWeight: 'bold',
    },
  },
  dialogContent: {
    backgroundColor: '#1a1a1a',
    paddingTop: '20px',
  },
  inputField: {
    '& .MuiInputBase-root': {
      color: 'white',
    },
    '& .MuiInputLabel-root': {
      color: '#bbb',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#444',
      },
      '&:hover fieldset': {
        borderColor: '#666',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4fc3f7',
      },
      backgroundColor: '#232323',
    },
    '& .MuiFormHelperText-root': {
      color: '#bbb',
    },
    marginBottom: '15px',
  },
  premiumBox: {
    backgroundColor: 'rgba(79, 195, 247, 0.1)',
    padding: theme.spacing(2),
    borderLeft: '4px solid #4fc3f7',
    borderRadius: '4px',
    marginBottom: theme.spacing(2),
  },
  dropArea: {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#232323',
    color: 'white',
    border: '2px dashed #444',
    '&:hover': {
      borderColor: '#4d79ff',
      backgroundColor: 'rgba(77, 121, 255, 0.1)',
    },
  },
  dropAreaActive: {
    borderColor: '#4d79ff',
    backgroundColor: 'rgba(77, 121, 255, 0.1)',
  },
  button: {
    backgroundColor: '#4d79ff',
    color: 'white',
    '&:hover': {
      backgroundColor: '#3a5bbf',
    },
    '&.Mui-disabled': {
      backgroundColor: '#333',
      color: '#888',
    },
    marginTop: 16,
    padding: '10px 20px',
  },
  premiumButton: {
    backgroundColor: '#4fc3f7',
    '&:hover': {
      backgroundColor: '#3da8d9',
    },
  },
  iconButton: {
    color: 'white',
  },
  whiteText: {
    color: 'white',
  },
  greyText: {
    color: '#bbb',
  },
  formControlLabel: {
    '& .MuiFormControlLabel-label': {
      color: 'white',
    },
  },
  statusMessage: {
    marginTop: 15,
  },
  successText: {
    color: '#4fc3f7',
  },
  errorText: {
    color: '#f44336',
  },
  select: {
    '& .MuiSelect-root': {
      color: 'white',
      backgroundColor: '#232323',
    },
    '& .MuiInputLabel-root': {
      color: '#bbb',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#444',
      },
      '&:hover fieldset': {
        borderColor: '#666',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4fc3f7',
      },
    },
    '& .MuiSelect-icon': {
      color: '#bbb',
    },
    marginBottom: '15px',
  },
  menuPaper: {
    backgroundColor: '#232323',
  },
  menuItem: {
    color: 'white',
    '&:hover': {
      backgroundColor: '#333',
    },
    '&.Mui-selected': {
      backgroundColor: '#444',
      '&:hover': {
        backgroundColor: '#555',
      },
    },
  },
  paperPreview: {
    backgroundColor: '#232323',
    padding: 8,
    position: 'relative',
    maxWidth: '100%',
  },
  deleteButton: {
    position: 'absolute',
    top: -10,
    right: -10,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: '4px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  cloudIcon: {
    fontSize: 48,
    color: '#4d79ff',
    marginBottom: 8,
  },
  checkbox: {
    color: '#4fc3f7',
    '&.Mui-checked': {
      color: '#4fc3f7',
    },
  },
  filesList: {
    backgroundColor: '#232323',
    borderRadius: '8px',
    maxHeight: '250px',
    overflowY: 'auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#1a1a1a',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#555',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#777',
    },
  },
  fileItem: {
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  primaryFileItem: {
    backgroundColor: 'rgba(79, 195, 247, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(79, 195, 247, 0.15)',
    },
  },
  fileIcon: {
    color: '#bbb',
  },
  imageFileIcon: {
    color: '#4d79ff',
  },
  documentFileIcon: {
    color: '#ff9800',
  },
  starIcon: {
    color: '#ffc107',
  },
  fileActions: {
    display: 'flex',
  },
  fileItemText: {
    '& .MuiTypography-root': {
      color: 'white',
    },
    '& .MuiTypography-body2': {
      color: '#bbb',
    },
  },
  sectionTitle: {
    color: 'white',
    fontWeight: 500,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  addMoreButton: {
    color: '#4d79ff',
    backgroundColor: 'rgba(77, 121, 255, 0.1)',
    marginTop: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'rgba(77, 121, 255, 0.2)',
    },
  },
  divider: {
    backgroundColor: '#333',
    margin: theme.spacing(2, 0),
  },
}));

const E3DToken_ADDRESS = "0x6488861b401F427D13B6619C77C297366bCf6386";
const NFTManager_ADDRESS = "0xeED4620ff525101Ffcf7327378232CA9EF778D47";
const MINT_FEE = ethers.utils.parseUnits("100", 18); // 100 E3D

const ACCEPTED_FILE_TYPES = {
  'image/png': true,
  'image/jpeg': true, 
  'image/gif': true,
  'image/webp': true,
  'image/svg+xml': true
};

const CustomNFTDialog = ({ open, onClose, setShowCustomNFTDialog, userAddress }) => {
  const classes = useStyles();
  const [nftName, setNftName] = useState("");
  const [nftDescription, setNftDescription] = useState("");
  const [listForSale, setListForSale] = useState(false);
  const [saleAmount, setSaleAmount] = useState("");
  const [saleCurrency, setSaleCurrency] = useState("E3D");
  const [isMinting, setIsMinting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [hasFreeMints, setHasFreeMints] = useState(false);
  const [remainingFreeMints, setRemainingFreeMints] = useState(0);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isAddingSupportingFile, setIsAddingSupportingFile] = useState(false);
  
  // Multi-file NFT state
  const [nftFiles, setNftFiles] = useState([]); // Array of { id, file, preview, isPrimary, name, type, size }
  const [primaryFileId, setPrimaryFileId] = useState(null);
  
  const fileInputRef = useRef(null);
  const supportingFileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  // Check subscription status when component mounts or userAddress changes
  useEffect(() => {
    const checkSubscription = async () => {
      if (!open || !userAddress) return;
      
      setIsLoadingSubscription(true);
      try {
        try {
          // Check if debug fake premium mode is active
          if (isDebugFakePremium()) {
            console.log('Debug mode: Using mock premium subscription status in NFT dialog');
            const mockDetails = getMockSubscriptionDetails(userAddress);
            setHasFreeMints(true);
            setRemainingFreeMints(mockDetails.remainingMints);
          } else {
            // Normal flow - check real subscription
            const details = await getSubscriptionDetails(userAddress);
            if (details && details.isActive && details.remainingMints > 0) {
              setHasFreeMints(true);
              setRemainingFreeMints(details.remainingMints);
            } else {
              setHasFreeMints(false);
              setRemainingFreeMints(0);
            }
          }
        } catch (error) {
          console.error("Error checking subscription:", error);
          setHasFreeMints(false);
        } finally {
          setIsLoadingSubscription(false);
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
        setHasFreeMints(false);
      } finally {
        setIsLoadingSubscription(false);
      }
    };
    
    checkSubscription();
  }, [open, userAddress]);

  // Set up event listeners for paste
  useEffect(() => {
    const handlePasteEvent = (event) => {
      if (!open) return;

      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            // Add as primary if no files exist yet, otherwise as supporting file
            addFile(file, nftFiles.length === 0);
            break;
          }
        }
      }
    };

    if (open) {
      window.addEventListener('paste', handlePasteEvent);
    }
    
    return () => {
      window.removeEventListener('paste', handlePasteEvent);
    };
  }, [open, nftFiles]);

  // Set up drag-and-drop event listeners
  useEffect(() => {
    const dropArea = dropAreaRef.current;
    if (!dropArea || !open) return;

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(true);
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        // Add first file as primary if no files exist yet
        addFile(files[0], nftFiles.length === 0);
        
        // Add any additional files as supporting files
        for (let i = 1; i < files.length; i++) {
          addFile(files[i], false);
        }
      }
    };

    dropArea.addEventListener('dragenter', handleDragEnter);
    dropArea.addEventListener('dragover', handleDragOver);
    dropArea.addEventListener('dragleave', handleDragLeave);
    dropArea.addEventListener('drop', handleDrop);

    return () => {
      dropArea.removeEventListener('dragenter', handleDragEnter);
      dropArea.removeEventListener('dragover', handleDragOver);
      dropArea.removeEventListener('dragleave', handleDragLeave);
      dropArea.removeEventListener('drop', handleDrop);
    };
  }, [dropAreaRef, isDragActive, open]);

  const handlePaste = (e) => {
    if (!open) return;
    
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        handleFileSelection(blob);
        break;
      }
    }
  };

  // Helper to check if a file is valid
  const isValidFile = (file) => {
    if (!file) return { valid: false, message: "No file selected" };
    
    // Check file type
    if (!ACCEPTED_FILE_TYPES[file.type]) {
      return { valid: false, message: "Please select a valid image file (PNG, JPEG, GIF, WEBP, SVG)" };
    }
    
    // Check file size
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      return { valid: false, message: "File too large. Please select an image smaller than 10MB" };
    }
    
    return { valid: true, message: "" };
  };
  
  // Generate a unique ID for files
  const generateFileId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Add a file to the NFT files collection
  const addFile = async (file, isPrimary = false) => {
    const validation = isValidFile(file);
    if (!validation.valid) {
      setStatusMessage(validation.message);
      return;
    }
    
    setStatusMessage("Checking content appropriateness...");
    
    try {
      // Use AI to check if the image content is appropriate
      const moderationResult = await checkImageAppropriate(file, (progress) => {
        setStatusMessage(progress.message);
      });
      
      if (!moderationResult.isAppropriate) {
        setStatusMessage(`Content not allowed: ${moderationResult.reason}`);
        return;
      }
      
      setStatusMessage("");
      
      // Create a new file object
      const fileId = generateFileId();
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileObj = {
          id: fileId,
          file,
          preview: e.target.result,
          isPrimary,
          name: file.name,
          type: file.type,
          size: file.size,
          moderationPassed: true, // Track that this file passed moderation
        };
        
        // Add to nftFiles
        setNftFiles(prevFiles => [...prevFiles, fileObj]);
        
        // Set as primary if it's the first file or explicitly marked as primary
        if (isPrimary || nftFiles.length === 0) {
          setPrimaryFileId(fileId);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error in content moderation:', error);
      setStatusMessage("Error checking content. Please try again.");
    }
  };
  
  // Handle primary file selection
  const handleFileSelection = (file) => {
    // First file added is always the primary
    addFile(file, nftFiles.length === 0);
  };

  // Handle input change for primary file
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  // Handle supporting file input change
  const handleSupportingFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      addFile(e.target.files[0], false); // Add as a supporting file (not primary)
    }
  };
  
  // Remove a file by ID
  const handleRemoveFile = (fileId) => {
    // Check if it's the primary file
    const isPrimary = fileId === primaryFileId;
    
    // Remove from nftFiles
    setNftFiles(prevFiles => {
      const newFiles = prevFiles.filter(f => f.id !== fileId);
      
      // If we removed the primary file and have other files left, set a new primary
      if (isPrimary && newFiles.length > 0) {
        setPrimaryFileId(newFiles[0].id);
      } else if (newFiles.length === 0) {
        setPrimaryFileId(null);
      }
      
      return newFiles;
    });
    
    // Reset file input if needed
    if (isPrimary && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Set a file as the primary one
  const handleSetPrimary = (fileId) => {
    setPrimaryFileId(fileId);
  };
  
  // Reset input fields for adding another supporting file
  const handleAddSupportingFile = () => {
    if (supportingFileInputRef.current) {
      supportingFileInputRef.current.value = '';
      supportingFileInputRef.current.click();
    }
  };

  const mintNFT = async (metadataURIs) => {
    try {
      if (!window.ethereum) {
        throw new Error("MetaMask is not installed");
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      
      // Get the NFT contract
      const nftContract = new ethers.Contract(
        NFTManager_ADDRESS,
        NFTManagerABI,
        signer
      );

      // Check if we have a free mint from subscription
      const usingFreeMint = hasFreeMints && remainingFreeMints > 0;
      const supportingFiles = nftFiles.filter(f => f.id !== primaryFileId);
      const primaryFile = nftFiles.find(f => f.id === primaryFileId);
      
      if (!primaryFile) {
        throw new Error("Primary file not found");
      }
      
      // Check if we need to approve tokens if not using free mints
      if (!usingFreeMint) {
        const mintFee = await nftContract.mintFeeE3D();
        const totalFee = mintFee.mul(supportingFiles.length + 1); // Fee for all NFTs
        
        // First approve E3D token spending
        const e3dContract = new ethers.Contract(
          E3DToken_ADDRESS,
          E3DTokenABI,
          signer
        );
        
        setStatusMessage("Approving E3D token spending...");
        const approveTx = await e3dContract.approve(
          NFTManager_ADDRESS,
          totalFee
        );
        await approveTx.wait();
        setStatusMessage("E3D token spending approved. Minting supporting NFTs...");
      }
      
      // Array to store supporting NFT IDs
      const supportingNftIds = [];
      
      // Mint supporting NFTs first
      for (let i = 0; i < supportingFiles.length; i++) {
        setStatusMessage(`Minting supporting NFT ${i + 1} of ${supportingFiles.length}...`);
        
        let mintTx;
        if (usingFreeMint && i < remainingFreeMints - 1) { // Save 1 free mint for primary
          mintTx = await nftContract.mintWithSubscription(metadataURIs.supportingMetadata[i]);
        } else {
          mintTx = await nftContract.mintNFT(metadataURIs.supportingMetadata[i]);
        }
        
        const receipt = await mintTx.wait();
        
        // Get the minted NFT ID from events
        const event = receipt.events.find(event => event.event === 'NFTMinted');
        if (event && event.args) {
          supportingNftIds.push(event.args.tokenId.toString());
        }
      }
      
      setStatusMessage("Supporting NFTs minted. Creating linked NFT contracts list...");
      
      // Construct linkedNFTContracts array
      const linkedNFTContracts = supportingNftIds.map(id => {
        return `${NFTManager_ADDRESS}.${id}`;
      });
      
      // Mint primary NFT with linked NFTs
      setStatusMessage("Minting primary NFT...");
      let primaryMintTx;
      if (usingFreeMint) {
        primaryMintTx = await nftContract.mintWithSubscriptionAndLinks(
          metadataURIs.primaryMetadata,
          linkedNFTContracts
        );
      } else {
        primaryMintTx = await nftContract.mintNFTWithLinks(
          metadataURIs.primaryMetadata,
          linkedNFTContracts
        );
      }
      
      const primaryReceipt = await primaryMintTx.wait();
      
      // Get the primary NFT ID from events
      const primaryEvent = primaryReceipt.events.find(event => event.event === 'NFTMinted');
      let primaryTokenId = null;
      
      if (primaryEvent && primaryEvent.args) {
        primaryTokenId = primaryEvent.args.tokenId.toString();
      } else {
        throw new Error("Failed to get primary NFT token ID");
      }

      // List primary NFT for sale if needed
      if (listForSale && primaryTokenId) {
        setStatusMessage("Listing primary NFT for sale...");
        const price = ethers.utils.parseEther(saleAmount);
        const isSaleE3D = saleCurrency === "E3D";
        
        const listTx = await nftContract.setNFTPrice(
          primaryTokenId,
          price,
          isSaleE3D
        );
        await listTx.wait();
      }

      return { 
        success: true, 
        primaryTokenId,
        supportingTokenIds: supportingNftIds
      };
    } catch (error) {
      console.error("❌ mintNFT error:", error);
      return { 
        success: false, 
        error: error.message 
      };
    }
  };

  const uploadToIPFS = async (files, name, description) => {
    try {
      const formData = new FormData();
      
      // Find primary file
      const primaryFile = files.find(f => f.id === primaryFileId);
      if (!primaryFile) throw new Error("Primary file is required");
      
      // Add primary file first
      formData.append("primaryFile", primaryFile.file);
      formData.append("name", name);
      formData.append("description", description);
      
      // Add supporting files
      const supportingFiles = files.filter(f => f.id !== primaryFileId);
      supportingFiles.forEach((file, index) => {
        formData.append(`supportingFile${index}`, file.file);
      });
      
      // Add metadata about supporting files
      formData.append("supportingFilesCount", supportingFiles.length);

      const response = await axios.post(`${SERVER_NAME}/uploadToIPFS/multi`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        // Return structured metadata for both primary and supporting files
        return {
          primaryMetadata: response.data.primaryMetadata,
          supportingMetadata: response.data.supportingMetadata || []
        };
      } else {
        throw new Error(response.data.error || "Upload failed");
      }
    } catch (error) {
      console.error("❌ uploadToIPFS error:", error);
      return null;
    }
  };

  const handleMint = async () => {
    if (!userAddress) {
      setStatusMessage("Please connect your wallet first.");
      return;
    }

    if (!nftName.trim()) {
      setStatusMessage("NFT Name is required.");
      return;
    }

    if (nftFiles.length === 0 || !primaryFileId) {
      setStatusMessage("Please select a primary image for your NFT.");
      return;
    }

    if (listForSale) {
      if (!saleAmount || isNaN(saleAmount) || Number(saleAmount) <= 0) {
        setStatusMessage("Sale amount must be a positive number.");
        return;
      }
    }
    
    // Check if text content (name and description) is appropriate
    setStatusMessage("Checking text content...");
    try {
      const textToCheck = `${nftName} ${nftDescription}`;
      const textResult = await checkTextAppropriate(textToCheck);
      
      if (!textResult.isAppropriate) {
        setStatusMessage(`Text content not allowed: ${textResult.reason}`);
        return;
      }
    } catch (error) {
      console.error("Error checking text content:", error);
      // We'll continue even if text check fails to avoid blocking users
    }
    
    // Count total NFTs to mint
    const totalNftsToMint = nftFiles.length;
    const supportingNftsCount = nftFiles.filter(f => f.id !== primaryFileId).length;
    
    // Check if user has enough free mints or warn about fees
    if (!hasFreeMints && totalNftsToMint > 1) {
      const confirmMultiple = window.confirm(
        `You are about to mint ${totalNftsToMint} NFTs (1 primary + ${supportingNftsCount} supporting).
        Each NFT requires a separate minting fee. Continue?`
      );
      
      if (!confirmMultiple) {
        return;
      }
    } else if (hasFreeMints && totalNftsToMint > remainingFreeMints) {
      const confirmExceedFreeMints = window.confirm(
        `You have ${remainingFreeMints} free mints remaining, but are trying to mint ${totalNftsToMint} NFTs. 
        Additional NFTs will require a minting fee. Continue?`
      );
      
      if (!confirmExceedFreeMints) {
        return;
      }
    }

    setIsMinting(true);
    setStatusMessage("Processing your NFT files...");

    try {
      // Upload all files to IPFS and get metadata URIs
      const metadataURIs = await uploadToIPFS(nftFiles, nftName, nftDescription);

      if (!metadataURIs || !metadataURIs.primaryMetadata) {
        throw new Error("Failed to upload files to IPFS");
      }

      setStatusMessage("Files uploaded. Beginning NFT minting process...");
      
      // Mint all NFTs with supporting ones first, then the primary
      const result = await mintNFT(metadataURIs);

      if (result.success) {
        // Show success with tokenId information
        const supportingCount = result.supportingTokenIds.length;
        setStatusMessage(
          `Successfully minted ${supportingCount + 1} NFTs! ` +
          `Primary NFT ID: ${result.primaryTokenId}, ` +
          `Supporting NFT IDs: ${supportingCount > 0 ? result.supportingTokenIds.join(', ') : 'none'}`
        );
        
        setTimeout(() => {
          setShowCustomNFTDialog(false);
        }, 3000);
      } else {
        setStatusMessage("Minting failed: " + result.error);
      }
    } catch (error) {
      console.error("❌ handleMint error:", error);
      setStatusMessage("Minting failed: " + error.message);
    }

    setIsMinting(false);
  };

  const handleClickUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md"
      PaperProps={{
        className: classes.dialogPaper
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6" className={classes.whiteText}>Create Custom NFT</Typography>
          </Box>
          <IconButton onClick={onClose} className={classes.iconButton}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      {isLoadingSubscription ? (
        <Box display="flex" justifyContent="center" p={2} alignItems="center" className={classes.dialogContent}>
          <CircularProgress size={20} style={{ marginRight: 10 }} />
          <Typography className={classes.whiteText}>Checking for premium benefits...</Typography>
        </Box>
      ) : hasFreeMints ? (
        <Box className={classes.premiumBox} mx={3}>

          <Box display="flex" alignItems="center">
            <CheckCircleIcon style={{ color: '#4fc3f7', marginRight: 8 }} />
            <Typography variant="body1" className={classes.whiteText} style={{ fontWeight: 500 }}>
              Premium Subscriber Benefit
            </Typography>
          </Box>
          <Typography variant="body2" className={classes.whiteText}>
            You have <Chip label={remainingFreeMints} size="small" style={{ margin: '0 4px', backgroundColor: '#4fc3f7', color: 'white' }} /> free NFT mints remaining from your premium subscription.
          </Typography>
        </Box>
      ) : null}

      <DialogContent className={classes.dialogContent} style={{ padding: '0 16px 24px 16px' }}>
        <TextField 
          fullWidth 
          label="NFT Name" 
          value={nftName} 
          onChange={(e) => setNftName(e.target.value)} 
          margin="normal" 
          variant="outlined"
          className={classes.inputField}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField 
          fullWidth 
          label="NFT Description" 
          multiline 
          rows={3} 
          value={nftDescription} 
          onChange={(e) => setNftDescription(e.target.value)} 
          margin="normal" 
          variant="outlined"
          className={classes.inputField}
          InputLabelProps={{
            shrink: true,
          }}
        />

        {/* NFT Files Section */}
        <Box mt={2} mb={3}>
          <Typography variant="body1" gutterBottom className={classes.whiteText} style={{ fontWeight: 500 }}>
            Primary NFT File
          </Typography>
          
          {/* Upload area for primary file if no files exist */}
          {nftFiles.length === 0 ? (
            <Box
              ref={dropAreaRef}
              borderRadius="borderRadius"
              p={3}
              textAlign="center"
              className={`${classes.dropArea} ${isDragActive ? classes.dropAreaActive : ''}`}
              onClick={handleClickUpload}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />
              <CloudUploadIcon className={classes.cloudIcon} />
              <Typography variant="body1" gutterBottom className={classes.whiteText}>
                Drag and drop primary image here, or click to browse
              </Typography>
              <Typography variant="body2" className={classes.greyText}>
                Supported formats: PNG, JPEG, GIF, WEBP, SVG (max 10MB)
              </Typography>
            </Box>
          ) : (
            <>
              {/* File list section */}
              <Typography variant="body2" className={classes.whiteText} style={{ marginBottom: '8px' }}>
                {nftFiles.length === 1 ? '1 file selected' : `${nftFiles.length} files selected`} - Primary file is marked with a star
              </Typography>
              
              {/* Scrollable file list */}
              <List className={classes.filesList}>
                {nftFiles.map((file) => {
                  const isPrimary = file.id === primaryFileId;
                  return (
                    <ListItem 
                      key={file.id} 
                      className={`${classes.fileItem} ${isPrimary ? classes.primaryFileItem : ''}`}
                    >
                      <ListItemIcon>
                        {file.type.startsWith('image/') ? (
                          <ImageIcon className={classes.imageFileIcon} />
                        ) : (
                          <FileCopyIcon className={classes.documentFileIcon} />
                        )}
                      </ListItemIcon>
                      <ListItemText 
                        primary={file.name} 
                        secondary={`${(file.size / 1024).toFixed(1)} KB`}
                        className={classes.fileItemText}
                      />
                      <ListItemSecondaryAction className={classes.fileActions}>
                        {!isPrimary ? (
                          <Tooltip title="Make primary">
                            <IconButton 
                              edge="end" 
                              size="small" 
                              onClick={() => handleSetPrimary(file.id)}
                            >
                              <StarBorderIcon fontSize="small" className={classes.fileIcon} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Primary file">
                            <IconButton 
                              edge="end" 
                              size="small" 
                              disabled
                            >
                              <StarIcon fontSize="small" className={classes.starIcon} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove file">
                          <IconButton 
                            edge="end" 
                            size="small" 
                            onClick={() => handleRemoveFile(file.id)}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
              
              {/* Add more files button */}
              <Box mt={2} display="flex" justifyContent="center">
                <input
                  ref={supportingFileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleSupportingFileInputChange}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  className={classes.addMoreButton}
                  onClick={handleAddSupportingFile}
                >
                  Add Supporting File
                </Button>
              </Box>
            </>
          )}
        </Box>

        <FormControlLabel
          className={classes.formControlLabel}
          control={
            <Checkbox 
              checked={listForSale} 
              onChange={(e) => setListForSale(e.target.checked)}
              className={classes.checkbox} 
            />
          }
          label="List NFT for Sale After Minting"
        />

        {listForSale && (
          <>
            <TextField
              fullWidth
              label="Sale Amount"
              value={saleAmount}
              onChange={(e) => setSaleAmount(e.target.value)}
              margin="normal"
              variant="outlined"
              className={classes.inputField}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Box className={classes.select}>
              <Select
                fullWidth
                value={saleCurrency}
                onChange={(e) => setSaleCurrency(e.target.value)}
                variant="outlined"
                MenuProps={{ 
                  classes: { paper: classes.menuPaper }
                }}
              >
                <MenuItem value="E3D" className={classes.menuItem}>E3D Token</MenuItem>
                <MenuItem value="ETH" className={classes.menuItem}>ETH</MenuItem>
              </Select>
            </Box>
          </>
        )}

        <Button
          variant="contained"
          onClick={handleMint}
          disabled={isMinting || !nftName.trim() || !primaryFileId || nftFiles.length === 0}
          className={`${classes.button} ${hasFreeMints ? classes.premiumButton : ''}`}
          startIcon={hasFreeMints ? <CheckCircleIcon /> : null}
        >
          {isMinting ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 10, color: 'white' }} />
              Processing...
            </>
          ) : (
            hasFreeMints ? `Mint NFT (Free)` : `Mint NFT (100 E3D)`
          )}
        </Button>

        {statusMessage && (
          <Typography 
            variant="body2" 
            className={`${classes.statusMessage} ${statusMessage.includes("failed") ? classes.errorText : classes.successText}`}
          >
            {statusMessage}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomNFTDialog;
